import { quoteApiConnector } from 'apiConnector/quote';
import type { IRequestForQuotes, QuoteResponseWithStatus } from 'apiConnector/quote/batchQuote';
import { quoteRequestApi } from 'apiConnector/quote/batchQuote';
import { BaseQueueEpicFactory } from 'epic/utils/queuing/baseQueueEpicFactory';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteAck } from 'models/quote/QuoteAck';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, ignoreElements, map, mergeMap, startWith, take, tap, withLatestFrom } from 'rxjs/operators';
import type { SpEpic } from 'shared/middlewares/epic/epicMiddleware.factory';
import type { Action } from 'state/actions';
import {
  batchQuoteAcked,
  batchQuoteRequested,
  batchQuoteRequestFailed,
  batchQuoteResultFailed,
} from 'state/batchForm/batchForm.action';
import { batchQuoteRemovedThunk, quoteResultReceivedThunk } from 'state/batchForm/batchForm.thunks';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { QuoteResultStatus } from 'state/quotes/results/result.model';
import type { PriceEnhancement } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { addElements } from 'state/utils/array';
import { dateNow } from 'utils/core/now';
import { fatEventApiConnector } from 'utils/streaming/api';
import { batchQuoteResult } from 'utils/streaming/messages';

export const BATCH_PRICING_MAX_QUEUE_SIZE = 5;

export type BatchQuoteResult = Readonly<{
  negotiationId: string;
  priceEnhancement?: PriceEnhancement;
  quoteDetails?: QuoteDetailsData;
  quoteExternalId?: string;
  quoteId: string;
  rejections?: NextWorkflowError[];
  solveResult?: number;
  status: QuoteResultStatus;
  formResult?: BatchFormResult;
}>;

export class BatchPricingQueueEpicFactory extends BaseQueueEpicFactory<string> {
  public addFormToQueueOnPricingRequestedEpic(): SpEpic {
    return actions$ =>
      actions$.pipe(
        ofType('BATCH_QUOTE_ADD_TO_QUEUE'),
        tap(action => action.formIds.length > 0 && this.addToQueue(...action.formIds)),
        map(action => batchQuoteRemovedThunk(action.tabId, action.formIds)),
      );
  }

  public priceFormEpic(): EpicWithThunk {
    return (
      actions$,
      state$,
      {
        httpClient,
        selectors: { getBatchFormVersion, getBatchFormQuoteQuery, getBatchFormProductSubtype, getBatchTabIdByFormId },
      },
    ) =>
      actions$.pipe(
        ofType('USER_CURRENT_RECEIVED'),
        take(1),
        mergeMap(() =>
          this.itemsToHandle$.pipe(
            withLatestFrom(state$),
            mergeMap(([formIds, state]) => {
              this.startHandling(...formIds);
              const tabId = getBatchTabIdByFormId(state, formIds[0]);

              const requestForQuotes: IRequestForQuotes = {
                quotes: formIds.map(formId => ({
                  negotiationId: formId,
                  version: getBatchFormVersion(state, formId)!,
                  quoteQuery: getBatchFormQuoteQuery(state, formId),
                  productSubType: getBatchFormProductSubtype(state, formId),
                })),
              };

              return of(dateNow()).pipe(
                mergeMap(nowInMillis =>
                  quoteRequestApi(httpClient)
                    .price(requestForQuotes)
                    .pipe(
                      mergeMap(response => {
                        const actions: Action[] = [];

                        const { acceptedForms, failedForms, timeoutForms } = this.getAcceptedAndFailedForms(
                          response.quotes,
                          formIds,
                        );
                        if (acceptedForms.length > 0) {
                          actions.push(batchQuoteAcked(tabId, acceptedForms, nowInMillis));
                        }
                        if (failedForms.length > 0) {
                          this.completeHandling(...failedForms);
                          actions.push(batchQuoteRequestFailed(failedForms.map(formId => ({ formId }))));
                        }
                        if (timeoutForms.length > 0) {
                          this.completeHandling(...timeoutForms.map(form => form.formId));
                          actions.push(batchQuoteRequestFailed(timeoutForms));
                        }

                        return actions;
                      }),
                      startWith(batchQuoteRequested(formIds, nowInMillis)),
                      catchError(() => {
                        this.completeHandling(...formIds);
                        return of(batchQuoteRequestFailed(formIds.map(formId => ({ formId }))));
                      }),
                    ),
                ),
              );
            }),
          ),
        ),
      );
  }

  public abortQuoteEpic(): EpicWithThunk {
    return (actions$, _state$, { httpClient }) => {
      return actions$.pipe(
        ofType('BATCH_QUOTE_ABORTED'),
        tap(({ formId }) => this.completeHandling(formId)),
        mergeMap(({ formId, quoteId }) => {
          return quoteApiConnector(httpClient)
            .cancelQuote({ negotiationId: formId, quoteId })
            .pipe(
              ignoreElements(),
              catchError(_ => EMPTY),
            );
        }),
      );
    };
  }

  public quoteResultReceivedEpic(): EpicWithThunk {
    return (actions$, state$, { oldHttpClient, frontLogger, streaming, selectors: { getBatchFormIdByQuoteId } }) =>
      actions$.pipe(
        ofType('USER_CURRENT_RECEIVED'),
        take(1),
        mergeMap(() =>
          batchQuoteResult(streaming).pipe(
            withLatestFrom(state$),
            mergeMap(([lightEvent, state]) =>
              fatEventApiConnector(oldHttpClient)
                .getFatEvent<BatchQuoteResult>(lightEvent)
                .pipe(
                  mergeMap(response => {
                    if (!response) {
                      return [];
                    }

                    this.completeHandling(response.negotiationId);
                    return of(quoteResultReceivedThunk(response.negotiationId, response.quoteId, response));
                  }),
                  catchError((error: Error) => {
                    frontLogger.error(
                      `An error occurred inside batchPricingQueueEpics.quoteResultReceivedEpic (@FrontError}`,
                      error,
                    );

                    if (!lightEvent.quoteId) {
                      return EMPTY;
                    }

                    const formId = getBatchFormIdByQuoteId(state, lightEvent.quoteId);
                    if (formId) {
                      this.completeHandling(formId);
                      return of(batchQuoteResultFailed(formId, lightEvent.quoteId));
                    }

                    return EMPTY;
                  }),
                ),
            ),
          ),
        ),
      );
  }

  public cancelQuotePushBackEpic(): EpicWithThunk {
    return (actions$, state$, { httpClient, selectors: { getBatchFormQuoteId } }) =>
      actions$.pipe(
        ofType('BATCH_QUOTE_PUSH_BACK_CANCEL_REQUESTED'),
        withLatestFrom(state$),
        mergeMap(([{ formId }, state]) => {
          this.completeHandling(formId);

          const quoteId = getBatchFormQuoteId(state, formId);

          if (!quoteId) {
            return EMPTY;
          }

          return quoteApiConnector(httpClient)
            .cancelQuote({ quoteId, negotiationId: formId })
            .pipe(
              map(() => batchQuoteResultFailed(formId, quoteId)),
              catchError(() => {
                return EMPTY;
              }),
            );
        }),
      );
  }

  private getAcceptedAndFailedForms(
    quoteResponse: readonly QuoteResponseWithStatus[],
    formIds: readonly string[],
  ): BatchPriceFormResponse {
    return quoteResponse.reduce<BatchPriceFormResponse>(
      (result, res, index) => {
        switch (res.status) {
          case 'Acknowledged':
            return { ...result, acceptedForms: addElements(result.acceptedForms, res.acknowledgement) };
          case 'Failed':
            return { ...result, failedForms: addElements(result.failedForms, formIds[index]) };
          case 'Timeout':
            return {
              ...result,
              timeoutForms: addElements(result.timeoutForms, {
                formId: formIds[index],
                newVersion: res.newVersion,
              }),
            };
          default:
            return result;
        }
      },
      {
        acceptedForms: [],
        failedForms: [],
        timeoutForms: [],
      },
    );
  }
}

export const batchPricingQueueEpics = new BatchPricingQueueEpicFactory(
  'batchPricingQueueEpics',
  BATCH_PRICING_MAX_QUEUE_SIZE,
);

interface BatchPriceFormResponse {
  acceptedForms: readonly QuoteAck[];
  failedForms: readonly string[];
  timeoutForms: ReadonlyArray<{ formId: string; newVersion: number }>;
}
